import axios from "./httpClient";
import * as _ from "lodash";

import { ApiType } from "./Types";
import { getUrl } from "./getURL";
import { ErrorMessages, STORAGE_KEYS } from "../constants";
import { IAuthUser } from "../models/user";

const MAX_RETRIES = 1;
const RETRY_DELAY_MS = 1000; // Delay between retries in milliseconds
const TOKEN_RETRY_DELAY_MS = 2000; // 2-second delay if the token is empty

/**
 * GET API call using Axios with retry mechanism and delay between retries.
 * If the token is empty, it will retry fetching the token with a delay.
 * @returns - Logged in User details type of IAuthUser
 */
export const validateOktaToken = async (): Promise<IAuthUser> => {
  const token = await fetchTokenWithRetry();
  return validateTokenWithRetry(token);
};

// Helper function to retry fetching the token
const fetchTokenWithRetry = async (): Promise<string> => {
  let tokenAttempts = 0;

  while (tokenAttempts < MAX_RETRIES) {
    const user = JSON.parse(localStorage.getItem(STORAGE_KEYS.OKTA_TOKEN_STORAGE)!);
    const token = _.get(user, "idToken.idToken");

    if (token) return token;

    tokenAttempts++;
    console.warn(
      `Token not found. Retrying in ${TOKEN_RETRY_DELAY_MS / 1000} seconds...`
    );
    await delay(TOKEN_RETRY_DELAY_MS);
  }

  throw new Error("Failed to retrieve the token after multiple attempts.");
};

// Helper function to validate token with retry mechanism
const validateTokenWithRetry = async (token: string): Promise<IAuthUser> => {
  let attempts = 0;

  while (attempts < MAX_RETRIES) {
    try {
      const response = await axios.post(
        getUrl(ApiType.VALIDATE_OKTA_TOKEN),
        { token });
      return response.data;
    } catch (error) {
      attempts++;
      if (attempts >= MAX_RETRIES) {
        console.error("Maximum retry attempts reached.");
        throw new Error(getErrorMessage(error));
      }

      console.warn(
        `Attempt ${attempts} failed. Retrying in ${
          RETRY_DELAY_MS / 1000
        } seconds...`
      );
      await delay(RETRY_DELAY_MS);
    }
  }

  throw new Error(ErrorMessages.validateOktaToken);
};

// Extracted error handling logic
const getErrorMessage = (error: any): string => {
  const status = _.get(error, "response.status");
  if (!status) throw new Error(ErrorMessages.statusCodeNotFound);
  if (status === 503) throw new Error(ErrorMessages.deploymentInprogressError);
  if (status === 500) return ErrorMessages.serverError;
  return (
    _.get(error, "response.data.error.message") ??
    _.get(error, "response.data.errors[0].message") ??
    ErrorMessages.validateOktaToken
  );
};

// Generic delay function
const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));
